import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../auth/SupabaseClient";
import {
  PlusCircle,
  Edit,
  Trash2,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface Size {
  id: string;
  name: string;
  description: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
}

interface SizesManagerProps {
  darkMode: boolean;
}

export default function SizesManager({ darkMode }: SizesManagerProps) {
  const [sizes, setSizes] = useState<Size[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSize, setEditingSize] = useState<Size | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<keyof Size>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const navigate = useNavigate();

  useEffect(() => {
    fetchSizes();
  }, []);

  const fetchSizes = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("sizes")
        .select("*")
        .order(sortField, { ascending: sortDirection === "asc" });

      if (error) throw error;
      setSizes(data || []);
    } catch (error: any) {
      console.error("Error fetching sizes:", error);
      setError(
        `Une erreur est survenue lors de la récupération des tailles: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEditingSize((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  const handleAddSize = async () => {
    if (!editingSize || !editingSize.name) {
      alert("Le nom de la taille est requis.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data, error } = await supabase
        .from("sizes")
        .insert([
          {
            name: editingSize.name,
            description: editingSize.description,
            owner_id: userData.user?.id,
          },
        ])
        .select();

      if (error) throw error;

      setSizes([...sizes, ...(data || [])]);
      setEditingSize(null);
      setIsEditing(false);
    } catch (error: any) {
      console.error("Error adding size:", error);
      setError(
        `Une erreur est survenue lors de l'ajout de la taille: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditSize = async () => {
    if (!editingSize || !editingSize.name) {
      alert("Le nom de la taille est requis.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("sizes")
        .update({
          name: editingSize.name,
          description: editingSize.description,
        })
        .eq("id", editingSize.id)
        .select();

      if (error) throw error;

      setSizes(
        sizes.map((size) =>
          size.id === editingSize.id && data?.[0] ? (data[0] as Size) : size
        )
      );
      setEditingSize(null);
      setIsEditing(false);
    } catch (error: any) {
      console.error("Error updating size:", error);
      setError(
        `Une erreur est survenue lors de la modification de la taille: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSize = async (id: string) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette taille ?"))
      return;

    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase.from("sizes").delete().eq("id", id);

      if (error) throw error;

      setSizes(sizes.filter((size) => size.id !== id));
    } catch (error: any) {
      console.error("Error deleting size:", error);
      setError(
        `Une erreur est survenue lors de la suppression de la taille: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingSize(null);
    setIsEditing(false);
  };

  const handleSort = (field: keyof Size) => {
    setSortDirection(
      field === sortField && sortDirection === "asc" ? "desc" : "asc"
    );
    setSortField(field);
  };

  const filteredSizes = sizes.filter(
    (size) =>
      size.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      size.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div
          className={`animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 ${
            darkMode ? "border-blue-400" : "border-blue-500"
          }`}
        ></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`text-red-500 text-center p-4 ${
          darkMode ? "bg-red-900" : "bg-red-100"
        } rounded-lg m-4`}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      className={`container mx-auto p-6 ${
        darkMode ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"
      }`}
    >
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des Tailles</h1>
        {!isEditing && (
          <button
            onClick={() => {
              setEditingSize({
                id: "",
                name: "",
                description: "",
                owner_id: "",
                created_at: "",
                updated_at: "",
              });
              setIsEditing(true);
            }}
            className={`px-4 py-2 rounded-lg flex items-center ${
              darkMode
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-blue-500 hover:bg-blue-600 text-white"
            }`}
          >
            <PlusCircle className="w-5 h-5 mr-2" />
            Ajouter une taille
          </button>
        )}
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
              darkMode ? "text-gray-400" : "text-gray-500"
            }`}
          />
          <input
            type="text"
            placeholder="Rechercher..."
            className={`w-full pl-10 pr-4 py-2 rounded-lg ${
              darkMode ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-800"
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {isEditing && (
        <div
          className={`${
            darkMode ? "bg-gray-800" : "bg-white"
          } shadow-md rounded-lg p-6 mb-6`}
        >
          <h2 className="text-xl font-semibold mb-4">
            {editingSize?.id
              ? "Modifier la Taille"
              : "Ajouter une Nouvelle Taille"}
          </h2>
          <div className="space-y-4">
            <input
              type="text"
              name="name"
              placeholder="Nom de la taille"
              value={editingSize?.name || ""}
              onChange={handleInputChange}
              className={`w-full p-2 border rounded-md ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-800"
              }`}
            />
            <textarea
              name="description"
              placeholder="Description"
              value={editingSize?.description || ""}
              onChange={handleInputChange}
              className={`w-full p-2 border rounded-md ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-800"
              }`}
              rows={3}
            />
            <div className="flex space-x-4">
              <button
                onClick={editingSize?.id ? handleEditSize : handleAddSize}
                className={`px-4 py-2 rounded-md ${
                  darkMode
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-green-500 hover:bg-green-600 text-white"
                }`}
              >
                {editingSize?.id ? "Modifier" : "Ajouter"}
              </button>
              <button
                onClick={handleCancel}
                className={`px-4 py-2 rounded-md ${
                  darkMode
                    ? "bg-gray-600 hover:bg-gray-700 text-white"
                    : "bg-gray-500 hover:bg-gray-600 text-white"
                }`}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-grow">
        <div
          className={`overflow-x-auto overflow-y-auto max-h-[calc(100vh-600px)] min-h-[calc(100vh-350px)] rounded-lg border ${
            darkMode ? "border-gray-600" : "border-gray-300"
          }`}
        >
          <div className="inline-block min-w-full align-middle">
            <table
              className={`min-w-full rounded-lg ${
                darkMode ? "bg-gray-700" : "bg-white"
              }`}
            >
              <thead
                className={`${
                  darkMode ? "bg-gray-800" : "bg-gray-100"
                } sticky top-0 z-10`}
              >
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-500"
                    } uppercase tracking-wider cursor-pointer`}
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center">
                      Nom
                      {sortField === "name" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-500"
                    } uppercase tracking-wider cursor-pointer`}
                    onClick={() => handleSort("description")}
                  >
                    <div className="flex items-center">
                      Description
                      {sortField === "description" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-500"
                    } uppercase tracking-wider`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody
                className={`${darkMode ? "bg-gray-900" : "bg-white"} divide-y ${
                  darkMode ? "divide-gray-700" : "divide-gray-200"
                }`}
              >
                {filteredSizes.map((size) => (
                  <tr
                    key={size.id}
                    className={
                      darkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                    }
                  >
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        darkMode ? "text-gray-300" : "text-gray-900"
                      }`}
                    >
                      {size.name}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        darkMode ? "text-gray-300" : "text-gray-500"
                      }`}
                    >
                      {size.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setEditingSize(size);
                          setIsEditing(true);
                        }}
                        className={`text-blue-600 hover:text-blue-900 ${
                          darkMode ? "hover:text-blue-400" : ""
                        } mr-4`}
                      >
                        <Edit className="w-5 h-5 inline"   />
                      </button>
                      <button
                        onClick={() => handleDeleteSize(size.id)}
                        className={`text-red-600 hover:text-red-900 ${
                          darkMode ? "hover:text-red-400" : ""
                        }`}
                      >
                        <Trash2 className="w-5 h-5 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}